import { db, functions } from '../firebase';
import {
  addDoc,
  collection,
  updateDoc,
  doc,
  getDoc,
  query,
  where,
  getDocs,
  writeBatch,
  serverTimestamp,
  orderBy,
  limit,
  startAfter,
  DocumentSnapshot,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import type { Recruitment, Application, User, RecruitmentStatusValue, DifficultyValue } from '../types';

type RecruitmentWithId = Recruitment & { id: string };
type ApplicationWithApplicant = Omit<Application, 'applicant'> & { applicant: User | null };

interface RecruitmentFilters {
  prefecture?: string;
  difficulty?: DifficultyValue;
  status?: RecruitmentStatusValue;
  userId?: string;
  orderBy?: string;
  orderDirection?: 'asc' | 'desc';
  limit?: number;
}

interface PaginationFilters {
  prefecture?: string;
  difficulty?: DifficultyValue;
  status?: RecruitmentStatusValue;
}

interface PaginationResult {
  recruitments: RecruitmentWithId[];
  lastVisible: DocumentSnapshot | null;
  hasMore: boolean;
}

interface RecruitmentStats {
  total: number;
  active: number;
  closed: number;
  cancelled: number;
  totalApplications: number;
}

export const recruitmentService = {
  // 募集を作成（serverTimestamp使用）
  async createRecruitment(recruitmentData: Omit<Recruitment, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, 'recruitments'), {
        ...recruitmentData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return docRef.id;
    } catch (error) {
      console.error('募集の作成に失敗しました:', error);
      throw error;
    }
  },

  // 募集を更新（serverTimestamp使用）
  async updateRecruitment(
    recruitmentId: string,
    updateData: Partial<Omit<Recruitment, 'id' | 'createdAt'>>
  ): Promise<boolean> {
    try {
      const recruitmentRef = doc(db, 'recruitments', recruitmentId);
      await updateDoc(recruitmentRef, {
        ...updateData,
        updatedAt: serverTimestamp(),
      } as Record<string, unknown>);
      return true;
    } catch (error) {
      console.error('募集の更新に失敗しました:', error);
      throw error;
    }
  },

  // 募集を削除（Cloud Functions 経由 - applications は Admin SDK で削除）
  async deleteRecruitment(recruitmentId: string): Promise<boolean> {
    try {
      const deleteRecruitmentFn = httpsCallable(functions, 'deleteRecruitment');
      await deleteRecruitmentFn({ recruitmentId });
      return true;
    } catch (error) {
      console.error('募集の削除に失敗しました:', error);
      throw error;
    }
  },

  // 募集を取得
  async getRecruitment(recruitmentId: string): Promise<RecruitmentWithId | null> {
    try {
      const recruitmentRef = doc(db, 'recruitments', recruitmentId);
      const recruitmentDoc = await getDoc(recruitmentRef);

      if (recruitmentDoc.exists()) {
        return { id: recruitmentDoc.id, ...recruitmentDoc.data() } as RecruitmentWithId;
      }
      return null;
    } catch (error) {
      console.error('募集の取得に失敗しました:', error);
      throw error;
    }
  },

  // 応募を取得（N+1問題解消）
  async getApplications(recruitmentId: string): Promise<ApplicationWithApplicant[]> {
    try {
      const applicationsQuery = query(
        collection(db, 'applications'),
        where('recruitmentId', '==', recruitmentId)
      );
      const querySnapshot = await getDocs(applicationsQuery);

      const applications: ApplicationWithApplicant[] = [];
      const userIds: string[] = [];

      // 応募データを収集し、ユーザーIDリストを作成
      querySnapshot.docs.forEach(appDoc => {
        const application = { id: appDoc.id, ...appDoc.data() } as ApplicationWithApplicant;
        applications.push(application);
        userIds.push(application.userId);
      });

      // ユーザーIDが重複しないようにフィルタリング
      const uniqueUserIds = Array.from(new Set(userIds));

      // バッチでユーザー情報を取得（最大30件まで）
      if (uniqueUserIds.length > 0) {
        const batchSize = 30;
        const userBatches: string[][] = [];

        for (let i = 0; i < uniqueUserIds.length; i += batchSize) {
          userBatches.push(uniqueUserIds.slice(i, i + batchSize));
        }

        const usersMap = new Map<string, User>();

        for (const batch of userBatches) {
          const usersQuery = query(
            collection(db, 'users'),
            where('uid', 'in', batch)
          );
          const usersSnapshot = await getDocs(usersQuery);

          usersSnapshot.docs.forEach(userDoc => {
            usersMap.set(userDoc.id, userDoc.data() as User);
          });
        }

        // 応募データにユーザー情報を付与
        applications.forEach(application => {
          application.applicant = usersMap.get(application.userId) ?? null;
        });
      }

      return applications;
    } catch (error) {
      console.error('応募の取得に失敗しました:', error);
      throw error;
    }
  },

  // ユーザーが既に応募しているかチェック
  async hasUserApplied(recruitmentId: string, userId: string): Promise<boolean> {
    try {
      const applicationsQuery = query(
        collection(db, 'applications'),
        where('recruitmentId', '==', recruitmentId),
        where('userId', '==', userId)
      );
      const querySnapshot = await getDocs(applicationsQuery);
      return !querySnapshot.empty;
    } catch (error) {
      console.error('応募チェックに失敗しました:', error);
      throw error;
    }
  },

  // 検索・フィルタリング機能
  async getRecruitments(filters: RecruitmentFilters = {}): Promise<RecruitmentWithId[]> {
    try {
      // conditions 配列にまとめてから1回でクエリを構築する（再構築パターンを廃止）
      const conditions = [];
      if (filters.prefecture) conditions.push(where('prefecture', '==', filters.prefecture));
      if (filters.difficulty) conditions.push(where('difficulty', '==', filters.difficulty));
      if (filters.status) conditions.push(where('status', '==', filters.status));
      if (filters.userId) conditions.push(where('userId', '==', filters.userId));

      const orderDirection = filters.orderDirection ?? 'desc';
      const sortField = filters.orderBy ?? 'createdAt';
      conditions.push(orderBy(sortField, orderDirection));

      if (filters.limit) conditions.push(limit(filters.limit));

      const q = query(collection(db, 'recruitments'), ...conditions);
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as RecruitmentWithId));
    } catch (error) {
      console.error('募集の取得に失敗しました:', error);
      throw error;
    }
  },

  // ページネーション対応の募集取得
  async getRecruitmentsWithPagination(
    filters: PaginationFilters = {},
    pageSize = 10,
    cursor: DocumentSnapshot | null = null
  ): Promise<PaginationResult> {
    try {
      const conditions = [];

      if (filters.prefecture) conditions.push(where('prefecture', '==', filters.prefecture));
      if (filters.difficulty) conditions.push(where('difficulty', '==', filters.difficulty));
      if (filters.status) conditions.push(where('status', '==', filters.status));

      conditions.push(orderBy('createdAt', 'desc'));

      if (cursor) conditions.push(startAfter(cursor));

      conditions.push(limit(pageSize));

      const q = query(collection(db, 'recruitments'), ...conditions);
      const snapshot = await getDocs(q);
      const recruitments = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as RecruitmentWithId));

      return {
        recruitments,
        lastVisible: snapshot.docs[snapshot.docs.length - 1] ?? null,
        hasMore: snapshot.docs.length === pageSize,
      };
    } catch (error) {
      console.error('募集の取得に失敗しました:', error);
      throw error;
    }
  },

  // 応募数を更新（バッチ処理）
  async updateApplicationCount(recruitmentId: string, increment = 1): Promise<boolean> {
    try {
      const batch = writeBatch(db);
      const recruitmentRef = doc(db, 'recruitments', recruitmentId);

      // 募集のapplicationCountを更新
      batch.update(recruitmentRef, {
        applicationCount: increment >= 0 ? increment : 0,
        updatedAt: serverTimestamp(),
      });

      await batch.commit();
      return true;
    } catch (error) {
      console.error('応募数の更新に失敗しました:', error);
      throw error;
    }
  },

  // 統計情報取得
  // TODO: 将来的には users/{userId}/stats/recruitments カウンタードキュメントに移行して全件取得を廃止する
  async getRecruitmentStats(userId: string | null = null): Promise<RecruitmentStats> {
    try {
      let q;
      if (userId) {
        // ユーザー固有統計: limit(500) で読み取り上限を設ける
        q = query(collection(db, 'recruitments'), where('userId', '==', userId), limit(500));
      } else {
        // 全体統計: 全件取得を避けるため上限を設ける
        q = query(collection(db, 'recruitments'), limit(500));
      }

      const snapshot = await getDocs(q);
      const recruitments = snapshot.docs.map(d => d.data() as Recruitment);

      return {
        total: recruitments.length,
        active: recruitments.filter(r => r.status === 'active').length,
        closed: recruitments.filter(r => r.status === 'closed').length,
        cancelled: recruitments.filter(r => r.status === 'cancelled').length,
        totalApplications: recruitments.reduce((sum, r) => sum + (r.applicationCount ?? 0), 0),
      };
    } catch (error) {
      console.error('統計情報の取得に失敗しました:', error);
      throw error;
    }
  },
};
